import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useUserProfile } from './useUserProfile';
import { UserProfile, UserRole } from './types';
import {
  INTEREST_MAPPINGS,
  ROLE_TEMPLATE_MAPPINGS,
} from './personalizationMappings';

export interface PersonalizationContextValue {
  /** User profile data */
  profile: UserProfile;
  /** Loading state */
  loading: boolean;
  /** Whether user has completed setup */
  isSetupComplete: boolean;
  /** Get tags relevant to user's interests */
  getRelevantTags: () => string[];
  /** Get tags relevant to user's role */
  getRoleTags: () => string[];
  /** Check if a tag is relevant to user */
  isTagRelevant: (tag: string) => boolean;
  /** Get preferred template types for user's role */
  getPreferredTemplateTypes: () => string[];
  /** Score an entity based on relevance to user */
  scoreEntityRelevance: (tags: string[], type?: string) => number;
  /** Get Q&A tags relevant to user */
  getQetaTags: () => string[];
  /** Get tech radar quadrants relevant to user */
  getRelevantQuadrants: () => string[];
  /** Get personalized greeting based on time and role */
  getPersonalizedGreeting: (displayName: string) => string;
  /** Get role-specific tips */
  getRoleTips: () => string[];
}

const PersonalizationContext =
  createContext<PersonalizationContextValue | null>(null);

interface PersonalizationProviderProps {
  children: ReactNode;
}

export const PersonalizationProvider = ({
  children,
}: PersonalizationProviderProps) => {
  const { profile, loading } = useUserProfile();

  const value = useMemo<PersonalizationContextValue>(() => {
    const isSetupComplete = profile.setupCompleted;

    const getRelevantTags = (): string[] => {
      const tags = new Set<string>();
      profile.interests.forEach(interest => {
        INTEREST_MAPPINGS[interest]?.tags.forEach(tag => tags.add(tag));
      });
      // Add division/platform context
      profile.divisions.forEach(div => tags.add(div.toLowerCase()));
      profile.platforms.forEach(plat => tags.add(plat.toLowerCase()));
      return Array.from(tags);
    };

    const getRoleTags = (): string[] => {
      if (!profile.role) return [];
      return ROLE_TEMPLATE_MAPPINGS[profile.role]?.tags || [];
    };

    const isTagRelevant = (tag: string): boolean => {
      const relevantTags = getRelevantTags();
      const roleTags = getRoleTags();
      const normalizedTag = tag.toLowerCase();
      return (
        relevantTags.some(
          t => normalizedTag.includes(t) || t.includes(normalizedTag),
        ) ||
        roleTags.some(
          t => normalizedTag.includes(t) || t.includes(normalizedTag),
        )
      );
    };

    const getPreferredTemplateTypes = (): string[] => {
      const types = new Set<string>();
      if (profile.role) {
        ROLE_TEMPLATE_MAPPINGS[profile.role]?.preferredTypes.forEach(t =>
          types.add(t),
        );
      }
      profile.interests.forEach(interest => {
        INTEREST_MAPPINGS[interest]?.templateCategories.forEach(t =>
          types.add(t),
        );
      });
      return Array.from(types);
    };

    const scoreEntityRelevance = (tags: string[], type?: string): number => {
      let score = 0;
      const relevantTags = getRelevantTags();
      const roleTags = getRoleTags();
      const preferredTypes = getPreferredTemplateTypes();

      // Score based on tag matches
      tags.forEach(tag => {
        const normalizedTag = tag.toLowerCase();
        if (relevantTags.some(t => normalizedTag.includes(t))) score += 2;
        if (roleTags.some(t => normalizedTag.includes(t))) score += 1;
      });

      // Score based on type match
      if (type && preferredTypes.some(t => type.toLowerCase().includes(t))) {
        score += 3;
      }

      // Bonus for division/platform match
      tags.forEach(tag => {
        const normalizedTag = tag.toLowerCase();
        if (
          profile.divisions.some(d => normalizedTag.includes(d.toLowerCase()))
        )
          score += 5;
        if (
          profile.platforms.some(p => normalizedTag.includes(p.toLowerCase()))
        )
          score += 5;
      });

      return score;
    };

    const getQetaTags = (): string[] => {
      const tags = new Set<string>();
      profile.interests.forEach(interest => {
        INTEREST_MAPPINGS[interest]?.qetaTags.forEach(tag => tags.add(tag));
      });
      return Array.from(tags);
    };

    const getRelevantQuadrants = (): string[] => {
      const quadrants = new Set<string>();
      profile.interests.forEach(interest => {
        INTEREST_MAPPINGS[interest]?.techRadarQuadrants.forEach(q =>
          quadrants.add(q),
        );
      });
      return Array.from(quadrants);
    };

    const getPersonalizedGreeting = (displayName: string): string => {
      const hour = new Date().getHours();
      let timeGreeting = 'Hello';
      if (hour < 12) timeGreeting = 'Good morning';
      else if (hour < 17) timeGreeting = 'Good afternoon';
      else timeGreeting = 'Good evening';

      const roleGreetings: Record<UserRole, string> = {
        developer: 'Ready to build something awesome?',
        'devops-engineer': "Let's streamline some pipelines!",
        'platform-engineer': 'Platform power at your fingertips!',
        'tech-lead': 'Leading the way in tech!',
        architect: 'Designing the future!',
        'data-engineer': 'Data awaits your expertise!',
        sre: 'Keeping systems reliable!',
        manager: 'Empowering your team!',
        other: 'Welcome!',
      };

      const roleMessage = profile.role
        ? roleGreetings[profile.role]
        : 'Welcome!';
      return `${timeGreeting}, ${displayName}! ${roleMessage}`;
    };

    const getRoleTips = (): string[] => {
      const tips: Record<UserRole, string[]> = {
        developer: [
          'Check out the API Explorer for available APIs',
          'Use Software Templates to bootstrap new services',
          'Star your frequently used components for quick access',
        ],
        'devops-engineer': [
          'Monitor your CI/CD pipelines in the catalog',
          'Check the Tech Radar for recommended tools',
          'Review infrastructure templates for best practices',
        ],
        'platform-engineer': [
          'Explore Backstage plugins to extend functionality',
          'Check InnerSource projects for collaboration',
          'Review platform documentation in TechDocs',
        ],
        'tech-lead': [
          "Review your team's components in the catalog",
          'Check ADRs for architecture decisions',
          'Monitor team metrics in the scorecard',
        ],
        architect: [
          'Use the catalog graph to visualize dependencies',
          'Review system documentation in TechDocs',
          'Check the Tech Radar for technology standards',
        ],
        'data-engineer': [
          'Explore data pipeline templates',
          'Check out data catalog components',
          'Review data documentation in TechDocs',
        ],
        sre: [
          'Monitor service health in the catalog',
          'Check observability tooling in Tech Radar',
          'Review incident documentation',
        ],
        manager: [
          'View team components and ownership',
          'Check team documentation in TechDocs',
          'Review project progress in the catalog',
        ],
        other: [
          'Explore the catalog to find components',
          'Use search to find documentation',
          'Check out Software Templates',
        ],
      };

      return profile.role ? tips[profile.role] : tips.other;
    };

    return {
      profile,
      loading,
      isSetupComplete,
      getRelevantTags,
      getRoleTags,
      isTagRelevant,
      getPreferredTemplateTypes,
      scoreEntityRelevance,
      getQetaTags,
      getRelevantQuadrants,
      getPersonalizedGreeting,
      getRoleTips,
    };
  }, [profile, loading]);

  return (
    <PersonalizationContext.Provider value={value}>
      {children}
    </PersonalizationContext.Provider>
  );
};

/**
 * Hook to access personalization context
 */
export const usePersonalization = (): PersonalizationContextValue => {
  const context = useContext(PersonalizationContext);
  if (!context) {
    throw new Error(
      'usePersonalization must be used within a PersonalizationProvider',
    );
  }
  return context;
};

/**
 * Hook that safely returns personalization or null if not in context
 */
export const usePersonalizationOptional =
  (): PersonalizationContextValue | null => {
    return useContext(PersonalizationContext);
  };
